import { FormEvent, useCallback, useState } from 'react';
import styled from 'styled-components';

import { deriveAgentDashboardUrl } from '@paradigm/terminal-core';

import { shortAddress } from '../chat-utils';
import { UrlInput } from '../chat.styles';

/**
 * Shown when the gateway rejects the handshake because this browser isn't
 * approved yet. The pairing token — if any — has been cleared by the
 * reconnect button, so the next connect goes out in role=pairing and the
 * operator can approve it via the OpenClaw CLI.
 */
export function PairingHelpBanner({
  accountId,
  clientIdPrefix,
  onRetry,
}: {
  readonly accountId: string;
  readonly clientIdPrefix: string;
  readonly onRetry: () => void;
}) {
  const shortAddr = shortAddress(accountId).replace('…', '');
  const clientId = `${clientIdPrefix}:${accountId
    .toLowerCase()
    .slice(0, 6)}${accountId.toLowerCase().slice(-4)}`;
  const approveCmd = `openclaw devices approve --latest`;
  const pairCmd =
    `openclaw devices pair \\\n` +
    `  --client-id ${clientId} \\\n` +
    `  --role operator \\\n` +
    `  --scopes operator.read,operator.write,operator.approvals`;

  const [copied, setCopied] = useState<'approve' | 'pair' | null>(null);
  const copy = useCallback((text: string, which: 'approve' | 'pair') => {
    // Fire-and-forget: the Clipboard API returns a promise but the button
    // handler is synchronous (void). If the API is unavailable the user
    // can still select-copy the visible text.
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(which);
        setTimeout(() => setCopied(null), 1500);
      })
      .catch(() => {
        /* no-op */
      });
  }, []);

  return (
    <PairingBanner>
      <PairingTitle>Pairing required for {shortAddr}</PairingTitle>
      <PairingText>
        This browser isn&apos;t approved to talk to your agent yet. On the
        machine running your OpenClaw agent, run one of:
      </PairingText>
      <PairingStep>
        <PairingStepLabel>
          1. Already requested? Approve the latest pending request:
        </PairingStepLabel>
        <PairingCmdRow>
          <PairingCmd>{approveCmd}</PairingCmd>
          <PairingCopyBtn
            type="button"
            onClick={() => copy(approveCmd, 'approve')}
          >
            {copied === 'approve' ? 'Copied' : 'Copy'}
          </PairingCopyBtn>
        </PairingCmdRow>
      </PairingStep>
      <PairingStep>
        <PairingStepLabel>
          2. Or mint a pairing directly for this browser:
        </PairingStepLabel>
        <PairingCmdRow>
          <PairingCmd>{pairCmd}</PairingCmd>
          <PairingCopyBtn type="button" onClick={() => copy(pairCmd, 'pair')}>
            {copied === 'pair' ? 'Copied' : 'Copy'}
          </PairingCopyBtn>
        </PairingCmdRow>
      </PairingStep>
      <PairingActions>
        <PairingPrimaryBtn type="button" onClick={onRetry}>
          I approved it — reconnect
        </PairingPrimaryBtn>
      </PairingActions>
    </PairingBanner>
  );
}

/**
 * Shown when the gateway rejects the handshake because the stored token no
 * longer matches (e.g. the OpenClaw instance was rotated or restarted).
 * The user fetches the new token from the dashboard and pastes it here.
 */
export function TokenMismatchBanner({
  agentUrl,
  accountId,
  onReconnect,
}: {
  readonly agentUrl: string;
  readonly accountId: string;
  readonly onReconnect: (token: string) => void;
}) {
  const [token, setToken] = useState('');
  const dashboardUrl = deriveAgentDashboardUrl(agentUrl);
  const shortAddr = shortAddress(accountId).replace('…', '');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = token.trim();
    if (trimmed.length === 0) return;
    onReconnect(trimmed);
  };

  return (
    <PairingBanner>
      <PairingTitle>Token rotated for {shortAddr}</PairingTitle>
      <PairingText>
        The gateway token no longer matches — it may have been rotated or the
        agent restarted. Open the dashboard to copy the current token, then
        paste it below.
      </PairingText>
      {dashboardUrl != null && (
        <PairingText>
          <a href={dashboardUrl} target="_blank" rel="noreferrer">
            {dashboardUrl}
          </a>
        </PairingText>
      )}
      <TokenMismatchForm onSubmit={handleSubmit}>
        <UrlInput
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Paste new gateway token"
          autoComplete="off"
          data-1p-ignore
        />
        <PairingPrimaryBtn type="submit" disabled={token.trim().length === 0}>
          Reconnect
        </PairingPrimaryBtn>
      </TokenMismatchForm>
    </PairingBanner>
  );
}

const PairingBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: rgba(var(--color-lightest), 0.04);
`;

const PairingTitle = styled.div`
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-weight: 600;
`;

const PairingText = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
  line-height: 1.45;
`;

const PairingStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PairingStepLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.6875rem;
`;

const PairingCmdRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const PairingCmd = styled.pre`
  flex: 1;
  margin: 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: var(--bg-color-page);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-family: var(--font-mono, monospace);
  font-size: 0.6875rem;
  white-space: pre-wrap;
  word-break: break-all;
`;

const PairingCopyBtn = styled.button`
  padding: 4px 8px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: none;
  color: var(--text-secondary);
  font-size: 0.625rem;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: var(--text-primary);
  }
`;

const PairingActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const TokenMismatchForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PairingPrimaryBtn = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: var(--button-color);
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;
